/**
 * TrackerSection Component
 * 
 * GPS Tracker management section for vehicle edit form.
 * Allows hosts to link/unlink tracker devices and view status.
 * Platform owners see additional controls and full device info.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  MapPin, 
  Satellite, 
  Clock, 
  AlertTriangle, 
  Check, 
  X, 
  Link2, 
  Unlink,
  RefreshCw,
  Shield,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const TrackerSection = ({ vehicle, onUpdate, isPlatformOwner = false }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [trackerStatus, setTrackerStatus] = useState(null);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkFormData, setLinkFormData] = useState({
    provider: '',
    device_id: '',
    notes: ''
  });

  const hasTracker = vehicle?.tracker_device_id;

  // Fetch tracker status
  const fetchTrackerStatus = async () => {
    if (!vehicle?.vehicle_id) return;
    
    try {
      const res = await api.get(`/tracker/vehicles/${vehicle.vehicle_id}/status`);
      setTrackerStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch tracker status:', err);
    }
  };

  useEffect(() => {
    if (hasTracker) {
      fetchTrackerStatus();
    }
  }, [vehicle?.vehicle_id, hasTracker]);

  const handleLinkTracker = async () => {
    if (!linkFormData.provider || !linkFormData.device_id) {
      toast.error('Please select a provider and enter device ID');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/tracker/vehicles/${vehicle.vehicle_id}/link`, linkFormData);
      toast.success('Tracker linked successfully');
      setShowLinkForm(false);
      setLinkFormData({ provider: '', device_id: '', notes: '' });
      if (onUpdate) onUpdate();
      fetchTrackerStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link tracker');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkTracker = async () => {
    if (!window.confirm('Are you sure you want to unlink this tracker?')) return;

    setLoading(true);
    try {
      await api.delete(`/tracker/vehicles/${vehicle.vehicle_id}/unlink`);
      toast.success('Tracker unlinked');
      setTrackerStatus(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink tracker');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-800 border-green-200';
      case 'offline': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return <Check className="w-3 h-3" />;
      case 'offline': return <X className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };

  return (
    <Card className="border-slate-200" id="tracker" data-testid="tracker-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Satellite className="w-5 h-5 text-blue-600" />
            GPS Tracker
            {trackerStatus?.recovery_mode_active && (
              <Badge className="bg-red-600 text-white animate-pulse">
                RECOVERY MODE
              </Badge>
            )}
          </CardTitle>
          {hasTracker && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchTrackerStatus}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Link a GPS tracker for vehicle security and recovery. Tracking data is only accessible during overdue bookings or recovery mode.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {!hasTracker ? (
          // No tracker linked
          <>
            {!showLinkForm ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                <Satellite className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600 mb-3">No tracker linked to this vehicle</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowLinkForm(true)}
                  data-testid="link-tracker-btn"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Link Tracker Device
                </Button>
              </div>
            ) : (
              // Link form
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-blue-800">Link GPS Tracker</h4>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowLinkForm(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-sm text-blue-700">Provider</Label>
                    <Select 
                      value={linkFormData.provider}
                      onValueChange={(v) => setLinkFormData(prev => ({ ...prev, provider: v }))}
                    >
                      <SelectTrigger className="bg-white" data-testid="tracker-provider-select">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="teltonika">Teltonika</SelectItem>
                        <SelectItem value="digital_matter">Digital Matter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm text-blue-700">Device ID / IMEI</Label>
                    <Input
                      value={linkFormData.device_id}
                      onChange={(e) => setLinkFormData(prev => ({ ...prev, device_id: e.target.value }))}
                      placeholder="e.g., 123456789012345"
                      className="bg-white"
                      data-testid="tracker-device-id-input"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-blue-700">Notes (optional)</Label>
                    <Textarea
                      value={linkFormData.notes}
                      onChange={(e) => setLinkFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="e.g., Installed under dashboard"
                      className="bg-white"
                      rows={2}
                    />
                  </div>

                  <Button 
                    onClick={handleLinkTracker}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="confirm-link-tracker-btn"
                  >
                    {loading ? 'Linking...' : 'Link Tracker'}
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          // Tracker linked - show status
          <div className="space-y-4">
            {/* Status card */}
            <div className={`p-4 rounded-lg border ${
              trackerStatus?.recovery_mode_active 
                ? 'bg-red-50 border-red-300' 
                : trackerStatus?.is_online 
                  ? 'bg-green-50 border-green-200'
                  : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(trackerStatus?.tracker_status)}>
                      {getStatusIcon(trackerStatus?.tracker_status)}
                      <span className="ml-1 capitalize">{trackerStatus?.tracker_status || 'Unknown'}</span>
                    </Badge>
                    <span className="text-sm text-slate-600">
                      {trackerStatus?.tracker_provider?.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className="text-sm text-slate-600">
                    <span className="font-medium">Device ID:</span>{' '}
                    {trackerStatus?.tracker_device_id || vehicle?.tracker_device_id?.slice(0, 8) + '***'}
                  </div>
                  
                  {trackerStatus?.last_seen_at && (
                    <div className="text-sm text-slate-600">
                      <span className="font-medium">Last seen:</span>{' '}
                      {new Date(trackerStatus.last_seen_at).toLocaleString()}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {!trackerStatus?.recovery_mode_active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnlinkTracker}
                      disabled={loading}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      data-testid="unlink-tracker-btn"
                    >
                      <Unlink className="w-4 h-4 mr-1" />
                      Unlink
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Location info - only show if recovery mode active or platform owner */}
            {(trackerStatus?.recovery_mode_active || isPlatformOwner) && trackerStatus?.last_location && (
              <div className="p-3 bg-slate-100 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Last Known Location</span>
                  {trackerStatus?.recovery_mode_active && (
                    <Badge className="bg-red-100 text-red-700 text-xs">Recovery Mode</Badge>
                  )}
                </div>
                <p className="text-sm text-slate-600">
                  {trackerStatus.last_location.address || 
                   `${trackerStatus.last_location.lat?.toFixed(5)}, ${trackerStatus.last_location.lng?.toFixed(5)}`}
                </p>
              </div>
            )}

            {/* Access control notice */}
            {!trackerStatus?.recovery_mode_active && !isPlatformOwner && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium">Location Access Restricted</p>
                  <p className="mt-0.5">
                    Location data is only accessible when a booking is overdue or recovery mode is activated.
                    This protects renter privacy during normal rentals.
                  </p>
                </div>
              </div>
            )}

            {/* Recovery mode banner */}
            {trackerStatus?.recovery_mode_active && (
              <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Recovery Mode Active</p>
                    <p className="text-xs text-red-600">
                      Full location tracking is enabled. Contact platform support for assistance.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrackerSection;
